;(function (angular, _) {
  'use strict'

  const STRINGS = {
    SAVED: 'Paiement sauvegardé !',
    UPDATED: 'Paiement modifié !',
    DELETED: 'Paiement supprimé !'
  }

  class PaymentService {
    constructor (
      // Angular
      // Libs
      $uibModal,
      // Managers
      CreditNoteManager, InvoiceManager,
      // Services
      DisplayService, SnackService, StatusService
      // Const
      // Resolved
    ) {
      this.uibModal = $uibModal
      this.displayService = DisplayService
      this.snackService = SnackService
      this.statusService = StatusService
      this.invoiceManager = InvoiceManager
      this.creditNoteManager = CreditNoteManager
    }

    // INVOICE
    /**
     * Delete payment at given index on given invoice
     * @param _invoice {Invoice}
     * @param paymentId {string}
     * @return {Promise<Invoice>}
     */
    deleteInvoicePayment (_invoice, paymentId) {
      const invoice = this._createNewInvoiceInstance(_invoice)
      return this.displayService.confirmDeletion().then(() => {
        return invoice.removePayment(paymentId)
          .then((result) => {
            this.snackService.snack({
              text: STRINGS.DELETED,
              delay: 5000
            })
            return result
          })
      })
    }

    /**
     * @param _invoice {Invoice}
     * @param payment {Payment}
     * @param paymentId {string}
     * @return {Promise<Invoice>}
     */
    updateInvoicePayment (_invoice, payment, paymentId) {
      if (payment.type === 'credit_note') {
        return new Promise((resolve, reject) => {
          reject(new Error('Can\'t update credit note payment'))
        })
      }
      const invoice = this._createNewInvoiceInstance(_invoice)
      return this.uibModal.open({
        controllerAs: 'makePayment',
        bindToController: true,
        resolve: {
          payment: () => { return angular.copy(payment) },
          balance: () => { return invoice.balanceDue - payment.amount.amount },
          invoiceId: () => { return invoice._id },
          isUpdate: () => { return true },
          clientId: () => { return invoice.client._id },
          creditNotes: () => { return 0 }
        },
        templateUrl: '/app/modules/invoice/show/templates/make-payment.template.html',
        controller: 'MakePaymentController'
      }).result.then((payment) => {
        return invoice
          .editPayment(paymentId, payment)
          .then((result) => {
            this.snackService.snack({
              text: STRINGS.UPDATED,
              delay: 5000
            })
            return result
          })
      })
    }

    /**
     * Add payment on given invoice
     * @param _invoice {Invoice}
     * @return {Promise<invoice>}
     */
    addInvoicePayment (_invoice, creditNote) {
      const invoice = this._createNewInvoiceInstance(_invoice)
      if (creditNote) {
        let payment = {
          amount: {
            amount: -creditNote.balanceDue,
            currency: 'EUR'
          },
          date: new Date(),
          type: 'credit_note',
          ref: {
            type: 'CreditNote',
            value: creditNote._id
          }
        }
        return invoice.addPayment(payment).then((result) => {
          return result
        })
      } else {
        return this.uibModal.open({
          controllerAs: 'makePayment',
          bindToController: true,
          resolve: {
            payment: angular.noop,
            creditNotes: () => {
              const creditNoteQuery = this.creditNoteManager
                .query()
                .select(invoice.client._id, 'client')
                .select(['validated', 'sent', 'partly_paid'], 'status')
              return this.creditNoteManager.count(creditNoteQuery)
            },
            balance: () => { return invoice.balanceDue },
            invoiceId: () => { return invoice._id },
            isUpdate: () => { return false },
            clientId: () => { return invoice.client._id }
          },
          templateUrl: '/app/modules/invoice/show/templates/make-payment.template.html',
          controller: 'MakePaymentController'
        }).result.then((payment) => {
          return invoice.addPayment(payment)
            .then((result) => {
              this.snackService.snack({
                text: STRINGS.SAVED,
                delay: 3000
              })
              return result
            })
        })
      }
    }

    // CREDIT NOTE

    /**
     * Delete payment at given index on given credit note
     * @param _creditNote {CreditNote}
     * @param paymentId {string}
     * @return {Promise<CreditNote>}
     */
    deleteCreditNotePayment (_creditNote, paymentId) {
      const creditNote = this._createNewCreditNoteInstance(_creditNote)
      return this.displayService.confirmDeletion().then(() => {
        return creditNote.removePayment(paymentId)
          .then((result) => {
            this.snackService.snack({
              text: STRINGS.DELETED,
              delay: 5000
            })
            return result
          })
      })
    }

    /**
     * Add payment on given credit note
     * @param _creditNote {CreditNote}
     * @return {Promise<CreditNote>}
     */
    addCreditNotePayment (_creditNote) {
      const creditNote = this._createNewCreditNoteInstance(_creditNote)
      return this.uibModal.open({
        controllerAs: 'makePayment',
        bindToController: true,
        resolve: {
          payment: angular.noop,
          invoices: () => {
            const invoiceQuery = this.invoiceManager
              .query()
              .select(creditNote.client._id, 'client')
              .select(['validated', 'sent', 'partly_paid', 'litigation', 'dunning'], 'status')
              .fields('_id number status balanceDue history payments')
            return this.invoiceManager.count(invoiceQuery)
          },
          balance: function () { return Math.round(creditNote.balanceDue) },
          creditNoteId: function () { return creditNote._id },
          clientId: function () { return creditNote.client._id },
          isUpdate: () => { return false },
        },
        templateUrl: '/app/modules/creditnote/show/templates/make-payment.template.html',
        controller: 'MakeCreditNotePaymentController'
      }).result.then((payment) => {
        return creditNote.addPayment(payment)
          .then((result) => {
            this.snackService.snack({
              text: STRINGS.SAVED,
              delay: 3000
            })
            return result
          })
      })
    }

    /**
     * @param _creditNote {CreditNote}
     * @param payment {Payment}
     * @param paymentId {string}
     * @return {Promise<CreditNote>}
     */
    updateCreditNotePayment (_creditNote, payment, paymentId) {
      if (payment.type === 'invoice') {
        return new Promise((resolve, reject) => {
          reject(new Error('Can\'t update invoice payment'))
        })
      }
      const creditNote = this._createNewCreditNoteInstance(_creditNote)
      return this.uibModal.open({
        controllerAs: 'makePayment',
        bindToController: true,
        resolve: {
          payment: () => { return angular.copy(payment) },
          invoices: () => {
            const invoiceQuery = this.invoiceManager
              .query()
              .select(creditNote.client._id, 'client')
              .select(['validated', 'sent', 'partly_paid', 'litigation', 'dunning'], 'status')
              .fields('_id number status balanceDue history payments')
            return this.invoiceManager.count(invoiceQuery)
          },
          balance: () => {
            return creditNote.balanceDue - payment.amount.amount
          },
          creditNoteId: function () { return creditNote._id },
          isUpdate: () => { return true },
          clientId: function () { return creditNote.client._id }
        },
        templateUrl: '/app/modules/creditnote/show/templates/make-payment.template.html',
        controller: 'MakeCreditNotePaymentController'
      }).result.then((payment) => {
        return creditNote
          .editPayment(paymentId, payment)
          .then((result) => {
            this.snackService.snack({
              text: STRINGS.UPDATED,
              delay: 5000
            })
            return result
          })
      })
    }

    // PRIVATES

    /**
     * Create a new invoice instance to prevent side effect on save (e.g.: invoice title has been updated elsewhere)
     * @param invoice {Invoice}
     * @return {Invoice}
     * @private
     */
    _createNewInvoiceInstance (invoice) {
      return this.invoiceManager.create(invoice)
    }

    /**
     * Create a new credit note instance to prevent side effect on save (e.g.: credit note title has been updated elsewhere)
     * @param creditNote {CreditNote}
     * @return {CreditNote}
     * @private
     */
    _createNewCreditNoteInstance (creditNote) {
      return this.creditNoteManager.create(creditNote)
    }
  }

  PaymentService.$inject = [
    // Angular
    // Libs
    '$uibModal',
    // Managers
    'CreditNoteManager', 'InvoiceManager',
    // Services
    'DisplayService', 'SnackService', 'StatusService'
    // Const
    // Resolved
  ]

  angular.module('kilo.common.services')
    .service('PaymentService', PaymentService)
})(window.angular, window._)
